import React from "react";
import { UIButton } from "@src/components/UIButton";
import { FEATURES } from "@src/constants";
import { theme } from "@src/theme";
import PlusIcon from "@assets/img/plus-icon.svg";
import PlusIconDark from "@assets/img/plus-icon-dark.svg";
import BackIcon from "@assets/img/back-icon.svg";
import {
  PlaylistBarContainer,
  PlaylistDetailsBarColContainer,
  PlaylistDetailsBarInfoTextRow,
  PlaylistDetailsBarInfoValueRow,
  PlaylistDetailsBarPlaylistInfoRow,
  PlaylistDetailsInfoContainer,
  SearchInputWrapper,
} from "./PlaylistDetailsBar.styles";
import { SearchInput } from "@src/components/SearchInput";
import { IPlaylistRecord, PlaylistStatus } from "@src/types/playlist";
import { IPlaylistTag } from "@src/types/playlistTag";
import { IPlaylistRecommendationTag } from "@src/types/playlistRecommendationTag";
import Tooltip from "@src/components/Tooltip";
import ArtWorkImageHoverPreview from "@src/components/ArtWorkImageHoverPreview";
import { getPlaylistCoverUrl } from "@src/utils/mediaUrl";
import { useRole } from "@src/hooks/useAuth";

type PlaylistDetailsBottomBarProps = {
  playlistData: IPlaylistRecord | null;
  onBack?: () => void;
  onAdd?: () => void;
  playlistStatus?: PlaylistStatus;
  showTitle?: string;
  playlistTitle?: string;
  playlistDescription?: string;

  onSearchChange?: (value: string) => void;
  searchValue?: string;

  onClick?: () => void;
  isMoodFactory?: boolean;
  onTagsClicked?: () => void;
  onRecommendationTagsClicked?: () => void;
  onJingleClicked?: () => void;
};
const PlaylistDetailsBottomBar: React.FC<PlaylistDetailsBottomBarProps> = ({
  playlistData,
  playlistStatus,
  showTitle = "",
  playlistTitle = "",
  playlistDescription = "",
  onBack,
  onAdd,
  onSearchChange,
  searchValue,
  onClick,
  isMoodFactory,
  onTagsClicked,
  onRecommendationTagsClicked,
  onJingleClicked,
}) => {
  const roleType = useRole();
  const canViewRecommendationTags =
    roleType === "admin" || roleType === "manager";
  const hasPlaylistAction =
    playlistData?.playlistAction === "crossfade" ||
    playlistData?.playlistAction === "cut";
  const nonPlaylistEditable = ["submitted", "aired", "archived", "scheduled"];
  const isPlaylistEditable =
    playlistStatus &&
    !nonPlaylistEditable.includes(playlistStatus) &&
    !hasPlaylistAction;
  return (
    <PlaylistBarContainer onClick={onClick}>
      <PlaylistDetailsBarColContainer
        style={{
          marginLeft: theme.margins.xl,
          marginRight: theme.margins.xl,
          gap: theme.margins.medium,
        }}
      >
        <UIButton
          onClick={onBack}
          variant="primaryOutline"
          style={{ minWidth: 90, fontWeight: "bold" }}
        >
          <img src={BackIcon} style={{ marginRight: theme.margins.small }} />
          Back
        </UIButton>
        <UIButton
          disabled={!isPlaylistEditable}
          onClick={isPlaylistEditable ? onAdd : () => {}}
          style={{ minWidth: 90, fontWeight: "bold" }}
          variant={!isPlaylistEditable ? "secondaryOutline" : "primary"}
        >
          <img
            src={!isPlaylistEditable ? PlusIcon : PlusIconDark}
            style={{ marginRight: theme.margins.small }}
          />
          Add
        </UIButton>
        <PlaylistDetailsInfoContainer>
          <PlaylistDetailsBarPlaylistInfoRow>
            <PlaylistDetailsBarInfoTextRow>
              {isMoodFactory ? "Mood:" : "Show:"}
            </PlaylistDetailsBarInfoTextRow>
            <PlaylistDetailsBarInfoValueRow>
              {showTitle}
            </PlaylistDetailsBarInfoValueRow>
          </PlaylistDetailsBarPlaylistInfoRow>
          <PlaylistDetailsBarPlaylistInfoRow>
            <ArtWorkImageHoverPreview
              value={getPlaylistCoverUrl(playlistData)}
              alt={playlistTitle ? `${playlistTitle} cover` : "Playlist cover"}
            >
              <PlaylistDetailsBarInfoTextRow>
                Playlist:
              </PlaylistDetailsBarInfoTextRow>
            </ArtWorkImageHoverPreview>
            <PlaylistDetailsBarInfoValueRow>
              {playlistTitle}
            </PlaylistDetailsBarInfoValueRow>
          </PlaylistDetailsBarPlaylistInfoRow>
          <PlaylistDetailsBarPlaylistInfoRow>
            <PlaylistDetailsBarInfoTextRow>
              Description:
            </PlaylistDetailsBarInfoTextRow>
            <PlaylistDetailsBarInfoValueRow>
              {playlistDescription}
            </PlaylistDetailsBarInfoValueRow>
          </PlaylistDetailsBarPlaylistInfoRow>
          <PlaylistDetailsBarPlaylistInfoRow>
            <PlaylistDetailsBarInfoTextRow>ID:</PlaylistDetailsBarInfoTextRow>
            <PlaylistDetailsBarInfoValueRow>
              {playlistData?.id}
            </PlaylistDetailsBarInfoValueRow>
          </PlaylistDetailsBarPlaylistInfoRow>
          <PlaylistDetailsBarPlaylistInfoRow
            onClick={(e) => e.stopPropagation()}
          >
            <PlaylistDetailsBarInfoTextRow>
              Curator:
            </PlaylistDetailsBarInfoTextRow>
            <PlaylistDetailsBarInfoValueRow>
              {playlistData?.curator?.name || ""}
            </PlaylistDetailsBarInfoValueRow>
          </PlaylistDetailsBarPlaylistInfoRow>
          <Tooltip
            content={onTagsClicked ? "Click to edit tags" : ""}
            position="top"
            key={playlistData?.id}
            tooltipId={`playlist-details-tags-tooltip-${playlistData?.id}`}
          >
            <PlaylistDetailsBarPlaylistInfoRow
              onClick={
                onTagsClicked
                  ? (e) => {
                      e.stopPropagation();
                      onTagsClicked();
                    }
                  : undefined
              }
              style={{ cursor: onTagsClicked ? "pointer" : "default" }}
            >
              <PlaylistDetailsBarInfoTextRow>
                Tags:
              </PlaylistDetailsBarInfoTextRow>
              <PlaylistDetailsBarInfoValueRow
                style={{ textTransform: "uppercase" }}
              >
                {[...(playlistData?.playlistTags ?? [])]
                  .sort((a, b) => a.displayOrder - b.displayOrder)
                  .map((tag: IPlaylistTag) => tag.label)
                  .join(", ")}
              </PlaylistDetailsBarInfoValueRow>
            </PlaylistDetailsBarPlaylistInfoRow>
          </Tooltip>
          {/* The subtags picked at submit, or by an admin since [ROVR-3982]. */}
          {FEATURES.PLAYLIST_SUBTAGS_ENABLED &&
            !!playlistData?.subPlaylistTags?.length && (
              <Tooltip
                content={onTagsClicked ? "Click to edit subtags" : ""}
                position="top"
                tooltipId={`playlist-details-subtags-tooltip-${playlistData?.id}`}
              >
                <PlaylistDetailsBarPlaylistInfoRow
                  onClick={
                    onTagsClicked
                      ? (e) => {
                          e.stopPropagation();
                          onTagsClicked();
                        }
                      : undefined
                  }
                  style={{ cursor: onTagsClicked ? "pointer" : "default" }}
                >
                  <PlaylistDetailsBarInfoTextRow>
                    Subtags:
                  </PlaylistDetailsBarInfoTextRow>
                  <PlaylistDetailsBarInfoValueRow
                    style={{ textTransform: "uppercase" }}
                  >
                    {[...playlistData.subPlaylistTags]
                      .sort(
                        (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
                      )
                      .map((subtag) => subtag.label)
                      .join(", ")}
                  </PlaylistDetailsBarInfoValueRow>
                </PlaylistDetailsBarPlaylistInfoRow>
              </Tooltip>
            )}
          {canViewRecommendationTags && (
            <Tooltip
              content={
                onRecommendationTagsClicked
                  ? "Click to edit recommendation tags"
                  : ""
              }
              position="top"
              tooltipId={`playlist-details-recommendation-tags-tooltip-${playlistData?.id}`}
            >
              <PlaylistDetailsBarPlaylistInfoRow
                onClick={
                  onRecommendationTagsClicked
                    ? (e) => {
                        e.stopPropagation();
                        onRecommendationTagsClicked();
                      }
                    : undefined
                }
                style={{
                  cursor: onRecommendationTagsClicked ? "pointer" : "default",
                }}
              >
                <PlaylistDetailsBarInfoTextRow>
                  Recommendation Tags:
                </PlaylistDetailsBarInfoTextRow>
                <PlaylistDetailsBarInfoValueRow>
                  {playlistData &&
                  Array.isArray(playlistData.playlistRecommendationTags)
                    ? playlistData.playlistRecommendationTags
                        .map((tag: IPlaylistRecommendationTag) => tag.title)
                        .join(", ")
                    : ""}
                </PlaylistDetailsBarInfoValueRow>
              </PlaylistDetailsBarPlaylistInfoRow>
            </Tooltip>
          )}
          {!!onJingleClicked && (
            <Tooltip
              content="Click to change jingle"
              position="top"
              tooltipId={`playlist-details-jingle-tooltip-${playlistData?.id}`}
            >
              <PlaylistDetailsBarPlaylistInfoRow
                onClick={(e) => {
                  e.stopPropagation();
                  onJingleClicked();
                }}
                style={{ cursor: "pointer" }}
              >
                <PlaylistDetailsBarInfoTextRow>
                  Jingle:
                </PlaylistDetailsBarInfoTextRow>
                <PlaylistDetailsBarInfoValueRow>
                  {playlistData?.jingle?.title || "—"}
                </PlaylistDetailsBarInfoValueRow>
              </PlaylistDetailsBarPlaylistInfoRow>
            </Tooltip>
          )}
        </PlaylistDetailsInfoContainer>
        <SearchInputWrapper>
          <SearchInput
            onChange={(e) => onSearchChange?.(e.target.value)}
            value={searchValue}
            placeholder="Search"
          />
        </SearchInputWrapper>
      </PlaylistDetailsBarColContainer>
    </PlaylistBarContainer>
  );
};

export default PlaylistDetailsBottomBar;
